import { Command } from "commander";
import { readFileSync, existsSync, writeFileSync, mkdirSync } from "node:fs";
import { resolve } from "node:path";
import chalk from "chalk";
import Table from "cli-table3";
import yaml from "js-yaml";
import { loadData } from "@fathom/core";
import { syncCalibration, logConvexStatus } from "../convex-client.js";
import { getProjectName } from "../project-name.js";
import { fatalError } from "../ux/errors.js";

interface TrackedSession {
  featureId?: string;
  status: string;
  inputTokens: number;
  outputTokens: number;
  model: string;
}

interface RegistryFeature {
  id: string;
  name: string;
  estimated_tokens: number;
}

export const calibrateCommand = new Command("calibrate")
  .description("Show calibration drift and profile adjustment suggestions")
  .option("-p, --project <name>", "Project name")
  .option("--registry <path>", "Path to registry.json")
  .option("--apply", "Write adjusted profiles to .fathom/calibrated-profiles.yaml")
  .action(async (options: { project?: string; registry?: string; apply?: boolean }) => {
    const registryPath =
      options.registry ??
      resolve(process.cwd(), ".fathom", "registry.json");
    const summaryPath = resolve(
      process.cwd(),
      ".fathom",
      "tracking",
      "summary.json",
    );

    if (!existsSync(registryPath) || !existsSync(summaryPath)) {
      console.log(
        chalk.yellow(
          "Need both registry and tracking data. Run `fathom analyze` then `fathom track` first.",
        ),
      );
      return;
    }

    let registry: { project?: string; features: RegistryFeature[] };
    try {
      registry = JSON.parse(readFileSync(registryPath, "utf-8"));
    } catch {
      fatalError("Invalid registry", "Failed to parse " + registryPath, "Delete and re-run fathom analyze");
    }
    let summary: { sessions: TrackedSession[] };
    try {
      summary = JSON.parse(readFileSync(summaryPath, "utf-8"));
    } catch {
      fatalError("Invalid tracking data", "Failed to parse " + summaryPath, "Delete and re-run fathom track --backfill");
    }
    const projectName = options.project ?? registry.project ?? getProjectName();

    // Aggregate actuals per feature
    const actuals = new Map<string, { tokens: number; count: number }>();
    for (const session of summary.sessions as TrackedSession[]) {
      if (!session.featureId || session.status === "untagged") continue;
      const existing = actuals.get(session.featureId) ?? {
        tokens: 0,
        count: 0,
      };
      existing.tokens += session.inputTokens + session.outputTokens;
      existing.count += 1;
      actuals.set(session.featureId, existing);
    }

    console.log(chalk.bold(`\nFathom — Calibrate: ${projectName}`));
    console.log(chalk.dim("─".repeat(55)));

    // Calculate drift per feature
    const drifts: number[] = [];
    const table = new Table({
      head: [
        chalk.white("Feature"),
        chalk.white("Estimated"),
        chalk.white("Actual"),
        chalk.white("Drift"),
        chalk.white("Adjustment"),
      ],
      colAligns: ["left", "right", "right", "center", "center"],
    });

    for (const feature of registry.features as RegistryFeature[]) {
      const actual = actuals.get(feature.id);
      if (!actual) continue;

      const drift =
        (actual.tokens - feature.estimated_tokens) / feature.estimated_tokens;
      drifts.push(drift);

      const driftPct = drift * 100;
      const driftLabel =
        Math.abs(driftPct) > 20
          ? driftPct > 0
            ? chalk.red(`+${driftPct.toFixed(0)}%`)
            : chalk.green(`${driftPct.toFixed(0)}%`)
          : chalk.white(`${driftPct >= 0 ? "+" : ""}${driftPct.toFixed(0)}%`);

      // Suggest adjustment multiplier
      const adjustmentMultiplier = 1 + drift * 0.3; // Dampen: only apply 30% of drift
      const adjLabel =
        Math.abs(drift) > 0.15
          ? chalk.cyan(`×${adjustmentMultiplier.toFixed(2)}`)
          : chalk.dim("—");

      table.push([
        feature.name,
        feature.estimated_tokens.toLocaleString(),
        actual.tokens.toLocaleString(),
        driftLabel,
        adjLabel,
      ]);
    }

    if (drifts.length === 0) {
      console.log(
        chalk.dim(
          "No features with both estimates and actuals. Need more tracking data.\n",
        ),
      );
      return;
    }

    console.log(table.toString());

    // Summary statistics
    const meanDrift =
      drifts.reduce((sum, d) => sum + d, 0) / drifts.length;
    const absDrifts = drifts.map(Math.abs);
    const mae = absDrifts.reduce((sum, d) => sum + d, 0) / absDrifts.length;

    console.log(chalk.dim(`\nCalibration summary (${drifts.length} features with data):`));
    console.log(
      `  Mean drift:   ${meanDrift >= 0 ? "+" : ""}${(meanDrift * 100).toFixed(1)}%`,
    );
    console.log(`  Mean abs err: ${(mae * 100).toFixed(1)}%`);
    console.log(
      `  Accuracy:     ${((1 - mae) * 100).toFixed(0)}%`,
    );

    if (mae > 0.25) {
      console.log(
        chalk.yellow(
          "\n  ⚠ High estimation error. Consider running `fathom analyze` with updated profiles.",
        ),
      );
    } else if (mae < 0.1) {
      console.log(
        chalk.green(
          "\n  ✓ Estimates are well-calibrated. Profiles are accurate.",
        ),
      );
    }

    // --apply: write calibrated profiles to disk
    if (options.apply) {
      // Load default profiles from @fathom/core data
      let defaultProfiles: Record<string, { input: number; output: number; total: number; notes?: string }>;
      try {
        const loaded = loadData();
        defaultProfiles = loaded.profiles;
      } catch {
        defaultProfiles = {};
      }

      const calibratedProfiles: Record<string, { input: number; output: number; total: number; notes: string }> = {};
      const applySummaryRows: { taskType: string; oldInput: number; newInput: number; oldOutput: number; newOutput: number; multiplier: number }[] = [];

      for (const feature of registry.features as RegistryFeature[]) {
        const actual = actuals.get(feature.id);
        if (!actual) continue;

        const drift = (actual.tokens - feature.estimated_tokens) / feature.estimated_tokens;
        if (Math.abs(drift) <= 0.15) continue; // Only include >15% drift

        const adjustmentMultiplier = 1 + drift * 0.3;
        const base = defaultProfiles[feature.id];
        if (!base) continue;

        const newInput = Math.round(base.input * adjustmentMultiplier);
        const newOutput = Math.round(base.output * adjustmentMultiplier);
        const newTotal = newInput + newOutput;

        calibratedProfiles[feature.id] = {
          input: newInput,
          output: newOutput,
          total: newTotal,
          notes: `Calibrated from ${actual.count} data point${actual.count === 1 ? "" : "s"} (confidence: ${Math.min(actual.count / 10, 1).toFixed(2)})`,
        };

        applySummaryRows.push({
          taskType: feature.id,
          oldInput: base.input,
          newInput,
          oldOutput: base.output,
          newOutput,
          multiplier: adjustmentMultiplier,
        });
      }

      if (Object.keys(calibratedProfiles).length > 0) {
        const fathomDir = resolve(process.cwd(), ".fathom");
        mkdirSync(fathomDir, { recursive: true });

        const yamlContent =
          `# Auto-generated by fathom calibrate --apply\n` +
          `# Generated: ${new Date().toISOString()}\n` +
          `# Overrides default task-profiles.yaml values based on tracked actuals\n` +
          yaml.dump({ profiles: calibratedProfiles }, { lineWidth: 120 });

        const calibratedFilePath = resolve(fathomDir, "calibrated-profiles.yaml");
        writeFileSync(calibratedFilePath, yamlContent, "utf-8");

        // Print summary table
        const applyTable = new Table({
          head: [
            chalk.white("Task Type"),
            chalk.white("Old Input"),
            chalk.white("New Input"),
            chalk.white("Old Output"),
            chalk.white("New Output"),
            chalk.white("Multiplier"),
          ],
          colAligns: ["left", "right", "right", "right", "right", "center"],
        });

        for (const row of applySummaryRows) {
          applyTable.push([
            row.taskType,
            row.oldInput.toLocaleString(),
            row.newInput.toLocaleString(),
            row.oldOutput.toLocaleString(),
            row.newOutput.toLocaleString(),
            chalk.cyan(`x${row.multiplier.toFixed(2)}`),
          ]);
        }

        console.log(chalk.bold("\n  Applied calibration profiles:"));
        console.log(applyTable.toString());
        console.log(
          chalk.green(
            `\n  Wrote ${Object.keys(calibratedProfiles).length} calibrated profile${Object.keys(calibratedProfiles).length === 1 ? "" : "s"} to ${calibratedFilePath}`,
          ),
        );
        console.log(
          chalk.dim(
            "  These will be used automatically by fathom estimate.",
          ),
        );
      } else {
        console.log(
          chalk.dim(
            "\n  No profiles with >15% drift to apply. Calibrated profiles file not written.",
          ),
        );
      }
    } else {
      console.log(
        chalk.dim(
          "\n  Tip: Run `fathom calibrate --apply` to write adjusted profiles for future estimates.",
        ),
      );
    }

    // Sync calibration data to Convex
    const profiles = (registry.features as RegistryFeature[])
      .filter((f) => actuals.has(f.id))
      .map((f) => {
        const actual = actuals.get(f.id)!;
        const drift = (actual.tokens - f.estimated_tokens) / f.estimated_tokens;
        const adjustment = 1 + drift * 0.3;
        return {
          taskType: f.id,
          baseInput: Math.round(f.estimated_tokens * 0.3 * adjustment),
          baseOutput: Math.round(f.estimated_tokens * 0.7 * adjustment),
          overheadMultipliers: {
            contextResend: 0.7,
            toolCalls: 0.15,
            thinking: 1.5,
            errorRecovery: 0.15,
            planning: 0.2,
            review: 0.1,
          },
          dataPoints: actual.count,
          confidence: Math.min(actual.count / 10, 1),
        };
      });

    const synced = await syncCalibration({ profiles });
    logConvexStatus(synced);
  });
